import { errorToast, formatResponse, isObject } from "@/shared/utils/helpers";
import {
  AppsmenError,
  Review,
  ReviewDiscountCode,
  ReviewProduct,
} from "@/shared/utils/interfaces";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { get2, patch } from "../axios";
import { generalToastId } from "../utils/constant";

interface Props {
  branchId: number;
  reviewId: string;
  generatorId?: number;
}

const useReviewHook = ({ branchId, reviewId, generatorId }: Props) => {
  const { t } = useTranslation();

  const getReview = useQuery<Review>({
    queryKey: ["review", reviewId, branchId],
    queryFn: () =>
      get2(`/internal/secure/pos/v1/order/reviews/${branchId}/${reviewId}`),
    select: (data) => formatResponse(data),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  useEffect(() => {
    if (getReview.isError) {
      errorToast();
    }
  }, [getReview.isError]);

  const data = useMemo(() => {
    return getReview.data === undefined ? undefined : getReview.data;
  }, [getReview.data]);

  const updateAsRead = useMutation({
    mutationFn: (itemId: number) => {
      return patch(
        `/internal/secure/pos/v1/order/reviews/read-state/${branchId}`,
        { read: [itemId] }
      );
    },
  });

  const getDiscountCodeQueryKey = ["review-discount-code", reviewId, branchId];
  const getDiscountCode = useQuery<ReviewDiscountCode, AppsmenError>({
    queryKey: getDiscountCodeQueryKey,
    queryFn: () =>
      get2(
        `internal/secure/pos/v1/order/reviews/create/discount/code/${branchId}/${getReview.data?.order_id}/${reviewId}/${generatorId}`
      ),
    select: (data) => formatResponse(data),
    retry: 0,
    refetchOnWindowFocus: false,
    enabled: false,
  });

  useEffect(() => {
    if (getDiscountCode.isError) {
      errorToast(
        getDiscountCode.failureReason?.response?.data?.error_message,
        generalToastId
      );
    }
  }, [getDiscountCode.isError]);

  const reply = useMutation({
    mutationFn: (body: any) =>
      patch(
        `/internal/secure/pos/v1/order/reviews/reply/${branchId}/${reviewId}`,
        body
      ),
    onSuccess: () => {
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast();
    },
  });

  const products = useMemo(() => {
    let products: ReviewProduct[] = [];
    if (data !== undefined) {
      if (isObject(data.product_name)) {
        Object.keys(data.product_name).forEach((key) => {
          let comment = data?.product_comment?.[key];
          let name = data?.product_name?.[key];
          let rating = parseInt(data?.product_rating?.[key] || "0");
          products.push({ name, comment, rating, id: parseInt(key) });
        });
      } else if (
        typeof data.product_comment === "string" &&
        typeof data.product_name === "string" &&
        typeof data.product_rating === "string"
      ) {
        products.push({
          comment: data.product_comment,
          name: data.product_name,
          rating: parseInt(data?.product_rating || "0"),
          id: 0,
        });
      }
    }
    return products;
  }, [data]);

  return {
    getReview,
    data,
    reply,
    products,
    updateAsRead,
    getDiscountCode,
    getDiscountCodeQueryKey,
  };
};

export default useReviewHook;
